import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  Link,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Circle as CircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

const HelpCenter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  
  const [expandedSection, setExpandedSection] = useState<string | false>('section1');

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  // Helper component for help sections
  const HelpSection = ({ title, content }: { title: string; content: React.ReactNode }) => (
    <Box mb={3}>
      <Typography variant="subtitle1" component="h3" fontWeight="bold" gutterBottom color="primary">
        {title}
      </Typography>
      {content}
    </Box>
  );

  // Helper component for bullet points
  const BulletPoint = ({ text }: { text: string }) => (
    <ListItem sx={{ py: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 28 }}>
        <CircleIcon sx={{ fontSize: 8, color: theme.palette.primary.light }} />
      </ListItemIcon>
      <ListItemText 
        primary={text} 
        primaryTypographyProps={{ 
          variant: 'body2',
          sx: { fontSize: '0.95rem' }
        }} 
      />
    </ListItem>
  );

  // Help center sections
  const helpSections = [
    {
      id: 'section1',
      title: '1. Getting Started',
      content: (
        <>
          <HelpSection 
            title="How to Create an Account" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Visit MyEventShop.com and click on Sign Up." />
                <BulletPoint text="Choose Creator (if you're hosting events) or Customer (if you're attending events)." />
                <BulletPoint text="Enter your email, name, and password, or sign up using Google/Facebook." />
                <BulletPoint text="Verify your email through the confirmation link sent to your inbox." />
              </List>
            }
          />
          
          <HelpSection 
            title="Navigating the MyEventShop Dashboard" 
            content={
              <List dense disablePadding>
                <BulletPoint text="The Dashboard is your control center." />
                <BulletPoint text="For Creators: View upcoming events, sales analytics, and marketing tools." />
                <BulletPoint text="For Customers: Browse events, manage bookings, and track event reminders." />
              </List>
            }
          />
          
          <HelpSection 
            title="Understanding User Roles: Customers vs. Creators" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Customers can browse, book, review, and share events." />
                <BulletPoint text="Creators can create, promote, and manage events while tracking engagement." />
              </List>
            }
          />
          
          <HelpSection 
            title="MyEventShop Mobile App: Features & Installation (Coming Soon)" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Key Features: Mobile event management, ticket scanning, push notifications." />
                <BulletPoint text="Available for iOS & Android (launch details to be announced)." />
              </List>
            }
          />
        </>
      )
    },
    {
      id: 'section2',
      title: '2. Event Creation & Management',
      content: (
        <>
          <HelpSection 
            title="How to Create an Event" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Go to Dashboard → Create New Event." />
                <BulletPoint text="Fill in event details (title, description, date, location, images)." />
                <BulletPoint text="Select ticket type: Paid, Free RSVP, or Donation-based." />
                <BulletPoint text="Set your event to Public (discoverable) or Private (invite-only)." />
                <BulletPoint text="Click Publish, and your event is live!" />
              </List>
            }
          />
          
          <HelpSection 
            title="Setting Up Ticketing & Pricing Options" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Choose between single-price tickets, tiered pricing, or group discounts." />
                <BulletPoint text="Enable early bird pricing or last-minute discounts." />
                <BulletPoint text="Add promo codes for VIPs or special attendees." />
              </List>
            }
          />
          
          <HelpSection 
            title="Managing RSVPs & Attendee Lists" 
            content={
              <List dense disablePadding>
                <BulletPoint text="View RSVPs in real-time under Event Dashboard." />
                <BulletPoint text="Download attendee lists for check-in purposes." />
                <BulletPoint text="Send reminders and updates directly to attendees." />
              </List>
            }
          />
          
          <HelpSection 
            title="How to Edit or Cancel an Event" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Go to Dashboard → Events → Edit Event." />
                <BulletPoint text="Update date, location, or details anytime before ticket sales start." />
                <BulletPoint text="To cancel an event, notify attendees and offer refunds (if applicable)." />
              </List>
            }
          />
          
          <HelpSection 
            title="Event Check-in & QR Code Scanning" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Attendees receive QR-coded tickets upon booking." />
                <BulletPoint text="Use MyEventShop's check-in app to scan QR codes at the venue." />
                <BulletPoint text="Track attendee numbers in real-time." />
              </List>
            }
          />
          
          <HelpSection 
            title="Understanding Event Analytics & Reports" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Monitor ticket sales, attendee demographics, and engagement metrics." />
                <BulletPoint text="Track marketing campaign success and conversion rates." />
                <BulletPoint text="Export reports for analysis and future planning." />
              </List>
            }
          />
        </>
      )
    },
    {
      id: 'section3',
      title: '3. Ticketing & Payments',
      content: (
        <>
          <HelpSection 
            title="How to Sell Tickets on MyEventShop" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Choose ticket type (Single, VIP, Group, Donation-based)." />
                <BulletPoint text="Set pricing, capacity, and ticket availability period." />
                <BulletPoint text="Enable waiting lists for sold-out events." />
              </List>
            }
          />
          
          <HelpSection 
            title="Setting Up Discounts & Promo Codes" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Offer percentage-based or fixed-amount discounts." />
                <BulletPoint text="Create time-sensitive discount codes for special offers." />
              </List>
            }
          />
          
          <HelpSection 
            title="Payment Processing & Accepted Methods" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Secure payments through Stripe, PayPal, Apple Pay, and Google Pay." />
                <BulletPoint text="Earnings are deposited directly into your linked bank account." />
              </List>
            }
          />
          
          <HelpSection 
            title="Refund & Cancellation Policies" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Refund policies are set by the event organizer." />
                <BulletPoint text="Customers can request refunds via the My Tickets section." />
              </List>
            }
          />
          
          <HelpSection 
            title="How and When Creators Get Paid" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Ticket sales earnings are transferred within 3–7 business days." />
                <BulletPoint text="Payment schedules depend on the payment gateway used." />
              </List>
            }
          />
        </>
      )
    },
    {
      id: 'section4',
      title: '4. Marketing & Promotion',
      content: (
        <>
          <HelpSection 
            title="How to Promote Events Using MyEventShop" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Utilize SEO-friendly event pages to improve visibility." />
                <BulletPoint text="Enable event sharing on social media." />
                <BulletPoint text="Use the email marketing tool to notify past attendees." />
              </List>
            }
          />
          
          <HelpSection 
            title="Social Media Scheduling & Integration" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Schedule posts directly from MyEventShop." />
                <BulletPoint text="Connect Facebook, Twitter, LinkedIn, and Instagram for automated sharing." />
              </List>
            }
          />
          
          <HelpSection 
            title="Sending Email Invitations & Newsletters" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Use pre-designed templates to send event invites." />
                <BulletPoint text="Segment audience lists for targeted email marketing." />
              </List>
            }
          />
          
          <HelpSection 
            title="Using Surveys & Feedback Forms" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Send pre-event polls to gauge interest." />
                <BulletPoint text="Collect post-event feedback to improve future events." />
              </List>
            }
          />
          
          <HelpSection 
            title="Boosting Event Visibility on MyEventShop" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Featured event placements are available for premium users." />
                <BulletPoint text="Engage with the MyEventShop community for organic promotion." />
              </List>
            }
          />
        </>
      )
    },
    {
      id: 'section5',
      title: '5. Customer Support & Troubleshooting',
      content: (
        <>
          <HelpSection 
            title="How to Contact Support" 
            content={
              <List dense disablePadding>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <EmailIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email: support@myeventshop.com" 
                    primaryTypographyProps={{ variant: 'body2' }} 
                  />
                </ListItem>
                {/* <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <PhoneIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Phone: Customer Support Number" 
                    primaryTypographyProps={{ variant: 'body2' }} 
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ChatIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Live Chat: Available on our website" 
                    primaryTypographyProps={{ variant: 'body2' }} 
                  />
                </ListItem> */}
              </List>
            }
          />
          
          <HelpSection 
            title="Resolving Payment Issues" 
            content={
              <List dense disablePadding>
                <BulletPoint text="If your payment fails, check your card details or PayPal account." />
                <BulletPoint text="Contact your bank or payment provider if issues persist." />
              </List>
            }
          />
          
          <HelpSection 
            title="Common Event Setup Issues & Fixes" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Event not publishing? Check required fields and internet connection." />
                <BulletPoint text="Ticket sales not processing? Verify your Stripe/PayPal integration." />
              </List>
            }
          />
          
          <HelpSection 
            title="Reporting Problems or Inappropriate Content" 
            content={
              <List dense disablePadding>
                <BulletPoint text='Report fraudulent events using the "Report Event" button.' />
              </List>
            }
          />
        </>
      )
    },
    {
      id: 'section6',
      title: '6. Community & Engagement',
      content: (
        <>
          <HelpSection 
            title="How to Follow Creators & Subscribe to Updates" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Click Follow on an event page to receive notifications." />
                <BulletPoint text="Subscribe to newsletters from your favorite creators." />
              </List>
            }
          />
          
          <HelpSection 
            title="Leaving Reviews & Ratings for Events" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Rate events from 1 to 5 stars." />
                <BulletPoint text="Leave detailed feedback for event organizers." />
              </List>
            }
          />
          
          <HelpSection 
            title="Joining & Participating in Event Discussions" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Engage in comment threads under event listings." />
                <BulletPoint text="Ask questions directly to event hosts." />
              </List>
            }
          />
          
          <HelpSection 
            title="Engaging with the MyEventShop Community" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Participate in online and offline networking events." />
              </List>
            }
          />
        </>
      )
    },
    {
      id: 'section7',
      title: '7. Account & Security',
      content: (
        <>
          <HelpSection 
            title="Updating Profile & Account Settings" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Edit your profile information under Settings." />
                <BulletPoint text="Change your password and notification preferences." />
              </List>
            }
          />
          
          <HelpSection 
            title="Password Reset & Account Recovery" 
            content={
              <List dense disablePadding>
                <BulletPoint text='Click "Forgot Password?" on the login page.' />
                <BulletPoint text="Follow the instructions sent to your registered email." />
              </List>
            }
          />
          
          <HelpSection 
            title="Managing Notifications & Privacy Settings" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Choose what type of notifications you want to receive." />
                <BulletPoint text="Adjust privacy settings to control data visibility." />
              </List>
            }
          />
          
          <HelpSection 
            title="Understanding GDPR & Data Protection Policies" 
            content={
              <List dense disablePadding>
                <BulletPoint text="MyEventShop is fully GDPR compliant." />
                <BulletPoint text="We never sell or misuse user data." />
              </List>
            }
          />
        </>
      )
    },
    {
      id: 'section8',
      title: '8. Integrations & Advanced Features',
      content: (
        <>
          <HelpSection 
            title="API Integration for Websites" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Use API keys to embed event listings on your site." />
                <BulletPoint text="Automate ticketing and customer interactions." />
              </List>
            }
          />
          
          <HelpSection 
            title="Connecting MyEventShop with External Platforms" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Integrate with CRM tools like HubSpot & Mailchimp." />
                <BulletPoint text="Sync with Google Calendar for event reminders." />
              </List>
            }
          />
          
          <HelpSection 
            title="Using Third-Party Social Logins" 
            content={
              <List dense disablePadding>
                <BulletPoint text="Log in with Google, Facebook, or LinkedIn." />
                <BulletPoint text="Secure authentication prevents unauthorized access." />
              </List>
            }
          />
        </>
      )
    }
  ];

  return (
    <Box>
      <AppMenu 
        onLoginClick={() => setIsLoginOpen(true)} 
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 5, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Help Centre
          </Typography>
          <Typography 
            variant="h6"
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto', 
              mb: 4,
              color: 'text.secondary'
            }}
          >
            Find answers to commonly asked questions and learn how to make the most of MyEventShop.
          </Typography>
        </Box>

        {/* Help Content */}
        <Paper 
          elevation={1} 
          sx={{ 
            borderRadius: 4, 
            p: { xs: 3, md: 4 }, 
            mb: 8 
          }}
        >
          {helpSections.map((section) => (
            <Accordion
              key={section.id}
              expanded={expandedSection === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{
                mb: 2,
                borderRadius: '8px !important',
                overflow: 'hidden',
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: '0 0 16px 0',
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: expandedSection === section.id 
                    ? `rgba(${parseInt(theme.palette.primary.main.slice(1, 3), 16)}, ${parseInt(theme.palette.primary.main.slice(3, 5), 16)}, ${parseInt(theme.palette.primary.main.slice(5, 7), 16)}, 0.08)`
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: `rgba(${parseInt(theme.palette.primary.main.slice(1, 3), 16)}, ${parseInt(theme.palette.primary.main.slice(3, 5), 16)}, ${parseInt(theme.palette.primary.main.slice(5, 7), 16)}, 0.05)`,
                  }
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  fontWeight="bold" 
                  color={expandedSection === section.id ? 'primary' : 'inherit'}
                >
                  {section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.01)',
                  borderTop: `1px solid ${theme.palette.divider}`,
                  pt: 3
                }}
              >
                {section.content}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>

        {/* Contact Box */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 8,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" component="h2" mb={2} fontWeight="bold">
            Need Additional Help?
          </Typography>
          <Typography variant="body1" mb={3}>
            Our customer support team is available to assist you with any issues not covered in our Help Centre.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Link 
              href="mailto:support@myeventshop.com" 
              underline="none" 
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              <EmailIcon sx={{ mr: 1 }} />
              Email Support
            </Link>
            {/* <Link 
              underline="none" 
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              <ChatIcon sx={{ mr: 1 }} />
              Live Chat
            </Link> */}
          </Box>
        </Paper>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default HelpCenter; 